import { useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  MARGEN_SUGERIDO_BP,
  costoDeUnidadVenta,
  formatMoney,
  formatQuantity,
  fromUnits,
  limpiarCodigo,
  mismoCodigo,
  newId,
  parseMoney,
  parseQuantity,
  precioParaMargen,
  type Centavos,
  type Milli,
} from '@abastece/shared';
import { db } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { costoConocido } from '../lib/costos.js';
import { emitir } from '../lib/emitir.js';
import {
  agregarCodigo,
  agregarCodigoAUnidad,
  desactivarProductos,
  etiquetaCortaLista,
  etiquetaListaPrecio,
  lecturaDeCodigo,
  ordenarListas,
  precioDe,
  preciosParaNueva,
  quitarCodigo,
  quitarCodigoDeUnidad,
  reactivarProductos,
  totalizar,
  unidadesDe,
} from '../lib/catalogo.js';
import { usePuede } from '../lib/sesion.js';
import { volver } from '../lib/volver.js';
import { Campo, Selector } from '../ui/Campo.js';
import { CampoCodigo } from '../ui/CampoCodigo.js';
import { CorregirCostoInventario } from '../ui/CorregirCostoInventario.js';
import { EditarProducto } from '../ui/EditarProducto.js';
import { Margen } from '../ui/Margen.js';
import { Pagina } from '../ui/Pagina.js';
import { Confirmar } from '../ui/Preguntar.js';
import { Ico } from '../ui/Iconos.js';
import { ElegirFoto } from '../ui/ElegirFoto.js';
import { Miniatura } from '../ui/Miniatura.js';
import { ponerFoto, quitarFoto } from '../lib/fotos.js';
import { useT } from '../i18n/index.js';
import { etiquetaCategoriaProducto, etiquetaLugar, etiquetaUnidad } from '../i18n/dominio.js';
import { nombreEnIngles, useNombreProducto } from '../i18n/productos.js';
import type { Producto, UnidadVenta } from '@abastece/core';

export function ProductoDetalle() {
  const t = useT();
  const nombre = useNombreProducto();
  const { productoId } = useParams({ from: '/productos/$productoId' });
  const puedeEditar = usePuede('editar_productos');
  /** What it sells for: the owner's (2026-10-13). A cashier who keeps the products reads the prices. */
  const puedePrecios = usePuede('editar_precios');
  const verCostos = usePuede('ver_costos');
  /** What the stock costs, set by hand: the owner's (2026-10-13). */
  const puedeCorregirCosto = usePuede('configurar');

  const producto = useLiveQuery(() => db.productos.get(productoId), [productoId], undefined);
  /** Every product, for the others that carry one of this one's codes. */
  const productos = useLiveQuery(() => db.productos.toArray(), [], []);
  const unidades = useLiveQuery(() => db.unidades.toArray(), [], []);
  const precios = useLiveQuery(() => db.precios.toArray(), [], []);
  const listas = useLiveQuery(() => db.listas.toArray(), [], []);
  const existencias = useLiveQuery(
    () => db.existencias.where('productoId').equals(productoId).toArray(),
    [productoId],
    [],
  );
  const lotes = useLiveQuery(
    () => db.lotes.where('productoId').equals(productoId).toArray(),
    [productoId],
    [],
  );
  const config = useLiveQuery(() => readSettings(), [], null);
  /** What it cost the last time it was bought, for a product no shelf holds at a cost now. */
  const compras = useLiveQuery(() => db.compras.toArray(), [], []);

  const [nuevaUnidad, setNuevaUnidad] = useState({ nombre: '', cantidad: '' });
  const [desactivando, setDesactivando] = useState(false);
  const [editando, setEditando] = useState(false);
  const [corrigiendoCosto, setCorrigiendoCosto] = useState(false);

  if (!producto) {
    return (
      <Pagina titulo={t('detalle.producto')} volverA="/productos">
        <p className="text-sm text-slate-400">{t('detalle.noEncontrado')}</p>
      </Pagina>
    );
  }

  const misUnidades = unidadesDe(unidades, productoId);
  /** The product's own codes, then each presentation's: what the till rings up for each. */
  const codigos: { codigo: string; unidad?: UnidadVenta }[] = producto
    ? [
        ...producto.codigosBarras.map((codigo) => ({ codigo })),
        ...misUnidades.flatMap((unidad) => (unidad.codigosBarras ?? []).map((codigo) => ({ codigo, unidad }))),
      ]
    : [];
  /**
   * The other products a code also rings up, each once: the same goods registered twice, or one
   * code a factory printed on two products. Said under the code, so that the till asking which
   * one is in the hand is no surprise.
   */
  const tambien = (codigo: string): Producto[] => [
    ...new Map(
      lecturaDeCodigo(productos, unidades, codigo, { inactivos: true })
        .filter(({ producto: otro }) => otro.id !== productoId)
        .map(({ producto: otro }) => [otro.id, otro]),
    ).values(),
  ];
  const hayCompartidos = codigos.some(({ codigo }) => tambien(codigo).some((otro) => otro.activo));
  const activas = ordenarListas(listas);
  const stock = totalizar(existencias);
  /**
   * What one base unit costs where it is known, as the till and the home screen's margin warning take
   * it: with nothing on the shelf, the stock's own cost is nothing, and there was no cost, no margin
   * and no price to suggest, while the home screen still warned of the margin (2026-10-13).
   */
  const costo = costoConocido(productoId, 'tienda', existencias, compras);
  /**
   * The shop's, the one place there is (the owner, 2026-10-05), and anything left somewhere else
   * under its place until «Traslado» brings it in.
   */
  const lugares = existencias.filter((e) => e.ubicacion === 'tienda' || e.cantidad !== 0);
  const minimoBp = config?.precioMargenMinimoBp ?? 1_000;

  async function fijarPrecio(unidadVentaId: string, listaId: string, texto: string) {
    const precio = parseMoney(texto);
    // Nothing, as «Precios» has it: a price of C$ 0 rang the product up free (2026-10-13).
    if (precio === null || precio <= 0) return;
    await emitir('precio.fijado', { unidadVentaId, listaId, precio });
  }

  /**
   * What the presentation being added starts at on each list, while its count is typed (the owner,
   * 2026-10-18): the box of 12 at 12 × the packet's (`preciosParaNueva`), said under the form and
   * set with it. Only for whoever sets prices; anybody else adds it unpriced, as before.
   */
  const factorNueva = parseQuantity(nuevaUnidad.cantidad);
  const deLaNueva = puedePrecios && factorNueva !== null && factorNueva > 0 ? preciosParaNueva(precios, misUnidades, activas, factorNueva) : [];
  const primera = deLaNueva[0];
  /** «Público C$ 120.00 · Mayorista C$ 108.00, 12 × el de «unidad»». */
  const precioDeLaNueva =
    primera && factorNueva !== null
      ? t('detalle.precioDeLaNueva', {
          precios: activas
            .flatMap((l) => deLaNueva.filter((s) => s.listaId === l.id).map((s) => `${etiquetaCortaLista(l)} ${formatMoney(s.precio)}`))
            .join(' · '),
          n: formatQuantity(Math.round((factorNueva * 1000) / primera.desde.factor) as Milli),
          unidad: etiquetaUnidad(primera.desde.nombre, primera.desde.nombre),
        })
      : null;

  async function agregarUnidad() {
    const factor = parseQuantity(nuevaUnidad.cantidad);
    if (!nuevaUnidad.nombre.trim() || factor === null || factor <= 0) return;
    const unidadVentaId = newId();
    await emitir('unidad_venta.creada', {
      unidadVentaId,
      productoId,
      nombre: nuevaUnidad.nombre.trim(),
      factor,
    });
    if (puedePrecios) {
      for (const { listaId, precio } of preciosParaNueva(precios, misUnidades, activas, factor)) {
        await emitir('precio.fijado', { unidadVentaId, listaId, precio });
      }
    }
    setNuevaUnidad({ nombre: '', cantidad: '' });
  }

  return (
    <Pagina
      titulo={nombre(producto)}
      // What the list says under each product, so what «Editar producto» changes shows here too.
      subtitulo={[
        t('detalle.subtitulo', {
          categoria: etiquetaCategoriaProducto(producto.categoria),
          unidad: etiquetaUnidad(producto.unidadBase, producto.unidadBase),
        }),
        ...(producto.esMedicamento ? [t('productos.medicamento')] : []),
        ...(producto.perecedero ? [t('productos.perecedero')] : []),
      ].join(' · ')}
      volverA="/productos"
      // A product taken off sale is listed under «Desactivados», and that is where it goes back to.
      volverBusqueda={producto.activo ? undefined : { vista: 'desactivados' }}
    >
      {!producto.activo && (
        <section
          role="status"
          className="mb-4 flex flex-col gap-3 rounded-2xl bg-amber-500/10 p-4 ring-1 ring-amber-500/20 ring-inset"
        >
          <p className="flex items-start gap-2 text-sm text-amber-200">
            <Ico icono="desactivar" className="mt-0.5 size-4 shrink-0" />
            {t('detalle.desactivado')}
          </p>
          {puedeEditar && (
            <button type="button" className="btn-secundario" onClick={() => void reactivarProductos([producto.id])}>
              <Ico icono="reiniciar" />
              {t('detalle.reactivar')}
            </button>
          )}
        </section>
      )}

      {/* --- Foto y nombres --- */}
      <section className="tarjeta mb-4 flex flex-col gap-4">
        {puedeEditar ? (
          <ElegirFoto
            producto={producto}
            onElegida={(huella) => ponerFoto(producto.id, huella)}
            onQuitada={() => quitarFoto(producto.id)}
          />
        ) : (
          <Miniatura producto={producto} tamano="xl" />
        )}
        <div>
          <span className="etiqueta">{t('producto.nombre')}</span>
          <p className="font-medium">{producto.nombre}</p>
        </div>
        {puedeEditar ? (
          <NombreEnIngles producto={producto} />
        ) : (
          nombreEnIngles(producto) && (
            <div>
              <span className="etiqueta">{t('producto.nombreEn')}</span>
              <p className="font-medium">{nombreEnIngles(producto)}</p>
            </div>
          )
        )}
        {producto.esMedicamento && producto.registroSanitario && (
          <div>
            <span className="etiqueta">{t('producto.registroSanitario')}</span>
            <p className="font-medium break-words">{producto.registroSanitario}</p>
          </div>
        )}
        {puedeEditar && (
          <button type="button" className="btn-secundario" onClick={() => setEditando(true)}>
            <Ico icono="editar" />
            {t('editarProducto.boton')}
          </button>
        )}
      </section>

      {/* --- Existencia --- */}
      <section className="tarjeta mb-4">
        <h2 className="mb-3 text-sm font-semibold text-slate-400">{t('detalle.existencia')}</h2>
        {lugares.length === 0 ? (
          <p className="text-sm text-slate-500">{t('detalle.sinExistencia')}</p>
        ) : (
          <div className="flex flex-col gap-2">
            {lugares.map((e) => (
              <div key={e.id} className="flex items-baseline justify-between">
                <span className="text-sm text-slate-400">
                  {lugares.length > 1 ? etiquetaLugar(e.ubicacion) : t('detalle.enExistencia')}
                </span>
                <span className={`tabular text-sm font-medium ${e.cantidad < 0 ? 'text-rose-400' : ''}`}>
                  {formatQuantity(e.cantidad)} {etiquetaUnidad(producto.unidadBase, producto.unidadBase)}
                  {verCostos && (
                    <span className="ml-2 text-slate-500">
                      {t('detalle.aCosto', {
                        monto: formatMoney(costoDeUnidadVenta(e.costoPromedio, fromUnits(1))),
                      })}
                    </span>
                  )}
                </span>
              </div>
            ))}
            {verCostos && (
              <div className="mt-2 flex items-baseline justify-between border-t border-slate-800 pt-2">
                <span className="text-sm font-medium">{t('detalle.valorTotal')}</span>
                <span className="tabular text-sm font-semibold">{formatMoney(stock.valor)}</span>
              </div>
            )}
            {stock.cantidad < 0 && (
              <p className="mt-2 rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
                {t('detalle.negativa')}
              </p>
            )}
          </div>
        )}
        {puedeCorregirCosto && (
          <button type="button" className="btn-secundario mt-3 w-full" data-corregir-costo-inventario onClick={() => setCorrigiendoCosto(true)}>
            <Ico icono="editar" />
            {t('costoInv.boton')}
          </button>
        )}
        {puedeEditar ? (
          <StockMinimo producto={producto} />
        ) : (
          producto.stockMinimo != null && (
            <p className="mt-3 text-xs text-slate-500">
              {t('detalle.minimoEs', {
                n: formatQuantity(producto.stockMinimo),
                unidad: etiquetaUnidad(producto.unidadBase, producto.unidadBase),
              })}
            </p>
          )
        )}
      </section>

      {/* --- Precios --- */}
      <section className="tarjeta mb-4 overflow-x-auto">
        <h2 className="mb-3 text-sm font-semibold text-slate-400">{t('detalle.presentaciones')}</h2>
        <table className="w-full min-w-[32rem] text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-500">
              <th className="pb-2 font-medium">{t('detalle.presentacion')}</th>
              {activas.map((l) => (
                <th key={l.id} className="pb-2 text-right font-medium">
                  {etiquetaListaPrecio(l)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {misUnidades.map((u) => {
              const costoUnidad = verCostos ? costoDeUnidadVenta(costo, u.factor) : null;
              return (
                <tr key={u.id} className="border-t border-slate-800">
                  <td className="py-3 pr-4">
                    <p className="font-medium">{etiquetaUnidad(u.nombre, u.nombre)}</p>
                    <p className="tabular text-xs text-slate-500">
                      {formatQuantity(u.factor)} {etiquetaUnidad(producto.unidadBase, producto.unidadBase)}
                      {costoUnidad !== null &&
                        costoUnidad > 0 &&
                        t('detalle.costoUnidad', { monto: formatMoney(costoUnidad) })}
                    </p>
                  </td>
                  {activas.map((l) => {
                    const actual = precioDe(precios, u.id, l.id);
                    return (
                      <td key={l.id} className="py-2 pl-2 text-right align-top">
                        <PrecioCelda
                          valor={actual}
                          editable={puedePrecios}
                          onGuardar={(texto) => void fijarPrecio(u.id, l.id, texto)}
                          sugerir={
                            costoUnidad && costoUnidad > 0
                              ? () =>
                                  void fijarPrecio(
                                    u.id,
                                    l.id,
                                    String(precioParaMargen(costoUnidad, MARGEN_SUGERIDO_BP) / 100),
                                  )
                              : undefined
                          }
                        />
                        {verCostos && actual !== null && costoUnidad !== null && costoUnidad > 0 && (
                          <div className="mt-1 flex justify-end">
                            <Margen precio={actual} costo={costoUnidad} minimoBp={minimoBp} compacto />
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>

        {puedeEditar && (
          <div className="mt-4 flex flex-wrap items-end gap-3 border-t border-slate-800 pt-4">
            <div className="min-w-40 flex-1">
              <Campo
                maxLength={40}
                etiqueta={t('detalle.nuevaPresentacion')}
                placeholder={t('detalle.ejemploPresentacion')}
                value={nuevaUnidad.nombre}
                onChange={(e) => setNuevaUnidad((n) => ({ ...n, nombre: e.target.value }))}
                autoComplete="off"
              />
            </div>
            <div className="w-32">
              <Campo
                etiqueta={t('detalle.enUnidad', {
                  unidad: etiquetaUnidad(producto.unidadBase, producto.unidadBase),
                })}
                placeholder="5"
                inputMode="decimal"
                className="tabular"
                value={nuevaUnidad.cantidad}
                onChange={(e) => setNuevaUnidad((n) => ({ ...n, cantidad: e.target.value }))}
              />
            </div>
            <button type="button" className="btn-secundario" onClick={() => void agregarUnidad()}>
              <Ico icono="agregar" />
              {t('comun.agregar')}
            </button>
            {precioDeLaNueva && (
              <p className="tabular w-full text-xs text-slate-500" data-presentacion-precio-nuevo>
                {precioDeLaNueva}
              </p>
            )}
          </div>
        )}
      </section>

      {/* --- Lotes --- */}
      {lotes.length > 0 && (
        <section className="tarjeta mb-4">
          <h2 className="mb-3 text-sm font-semibold text-slate-400">{t('detalle.lotes')}</h2>
          <div className="flex flex-col gap-2">
            {[...lotes]
              .sort((a, b) => a.vencimiento.localeCompare(b.vencimiento))
              .map((l) => (
                <div key={l.id} className="flex items-baseline justify-between text-sm">
                  <span className="font-medium">{l.numero}</span>
                  <span className="tabular text-slate-400">
                    {t('detalle.vence', {
                      fecha: l.vencimiento,
                      cantidad: formatQuantity(l.cantidadRecibida),
                      unidad: etiquetaUnidad(producto.unidadBase, producto.unidadBase),
                    })}
                  </span>
                </div>
              ))}
          </div>
        </section>
      )}

      {/* --- Códigos de barras --- */}
      {(puedeEditar || codigos.length > 0) && (
        <section className="tarjeta">
          <h2 className="mb-2 text-sm font-semibold text-slate-400">{t('detalle.codigos')}</h2>
          {codigos.length === 0 ? (
            <p className="text-sm text-slate-500">{t('detalle.sinCodigos')}</p>
          ) : (
            <ul className="flex flex-col divide-y divide-slate-800">
              {codigos.map(({ codigo: c, unidad }) => (
                <li key={`${unidad?.id ?? 'producto'}:${c}`} className="flex min-h-11 items-center justify-between gap-3 py-1">
                  <span className="min-w-0 text-sm">
                    <span className="tabular block break-all">{c}</span>
                    {/* With several presentations, which one the code rings up. */}
                    {misUnidades.length > 1 && (
                      <span className="block text-xs text-slate-500">
                        {etiquetaUnidad((unidad ?? misUnidades[0]!).nombre, (unidad ?? misUnidades[0]!).nombre)}
                      </span>
                    )}
                    {tambien(c).map((otro) => (
                      <Link
                        key={otro.id}
                        to="/productos/$productoId"
                        params={{ productoId: otro.id }}
                        className="btn-texto flex justify-start gap-1 text-left text-xs text-teal-300"
                        data-tambien={otro.id}
                      >
                        {t(otro.activo ? 'codigo.tambienLoLleva' : 'codigo.tambienLoLlevaDesactivado', {
                          producto: nombre(otro),
                        })}
                        <Ico icono="adelante" className="size-3.5 shrink-0" />
                      </Link>
                    ))}
                  </span>
                  {puedeEditar && (
                    <button
                      type="button"
                      className="btn-texto shrink-0 gap-1.5 text-sm text-slate-400"
                      onClick={() => void (unidad ? quitarCodigoDeUnidad(unidad.id, c) : quitarCodigo(producto.id, c))}
                    >
                      <Ico icono="quitar" className="size-4" />
                      {t('comun.quitar')}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {hayCompartidos && <p className="mt-2 text-xs text-slate-500">{t('codigo.sePreguntaCual')}</p>}
          {puedeEditar && <NuevoCodigo producto={producto} unidades={misUnidades} />}
        </section>
      )}

      {/* --- Desactivar --- */}
      {puedeEditar && producto.activo && (
        <section className="tarjeta mt-4">
          <p className="mb-3 text-sm text-slate-400">{t('detalle.ayudaDesactivar')}</p>
          <button type="button" className="btn-secundario w-full sm:w-auto" onClick={() => setDesactivando(true)}>
            <Ico icono="desactivar" />
            {t('detalle.desactivar')}
          </button>
        </section>
      )}

      {editando && (
        <EditarProducto producto={producto} existencias={existencias} onCerrar={() => setEditando(false)} />
      )}
      {corrigiendoCosto && (
        <CorregirCostoInventario producto={producto} existencias={existencias} onCerrar={() => setCorrigiendoCosto(false)} />
      )}

      {desactivando && (
        <Confirmar
          titulo={t('detalle.desactivarTitulo', { nombre: nombre(producto) })}
          mensaje={t('detalle.desactivarMensaje')}
          confirmar={t('preguntar.desactivar')}
          icono="desactivar"
          peligro
          onCerrar={() => setDesactivando(false)}
          onConfirmar={() => {
            // Back where the product was opened from: the list, which says so in «Desactivados»,
            // or «Inventario», «Análisis», wherever it was.
            void desactivarProductos([producto.id]).then(() => volver({ to: '/productos' }));
          }}
        />
      )}
    </Pagina>
  );
}

/**
 * One more code for this product: scanned off the can, or typed from the digits under it.
 *
 * A product can carry several, each kept exactly as it was read, so the list shows what is
 * printed on the packaging. With several presentations, the code says which one it rings up:
 * the box's code, printed on the box, is the box's — a case scanned as one can is a case sold
 * for the price of a can. A code for the smallest presentation stays the product's own.
 *
 * So with several, nothing is chosen until somebody chooses, for each code. The choice used to
 * start on the smallest, under the box a scanner types into, and a scanner's Enter saves at
 * once: a case's code scanned before anybody looked down was saved as the bottle's. Now that
 * Enter asks which, and the code waits in the box.
 */
function NuevoCodigo({ producto, unidades }: { producto: Producto; unidades: readonly UnidadVenta[] }) {
  const t = useT();
  const [texto, setTexto] = useState('');
  /** Empty: nobody has said. With one presentation that is the product's own code anyway. */
  const [unidadId, setUnidadId] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** Tried to add it before saying which presentation: said next to the question. */
  const [preguntado, setPreguntado] = useState(false);
  const limpio = limpiarCodigo(texto);
  const loTiene =
    Boolean(limpio) &&
    [...producto.codigosBarras, ...unidades.flatMap((u) => u.codigosBarras ?? [])].some((c) => mismoCodigo(c, limpio));
  const varias = unidades.length > 1;
  const falta = varias && !unidades.some((u) => u.id === unidadId);

  async function agregar(id = unidadId) {
    if (!limpio || loTiene) return;
    if (varias && !unidades.some((u) => u.id === id)) {
      setPreguntado(true);
      return;
    }
    setError(null);
    try {
      if (id !== '' && id !== unidades[0]?.id) await agregarCodigoAUnidad(id, limpio);
      else await agregarCodigo(producto.id, limpio);
      setTexto('');
      // The next code is its own question: it may well be the other presentation's.
      setUnidadId('');
      setPreguntado(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('producto.noSePudoGuardar'));
    }
  }

  return (
    <div className="mt-4 border-t border-slate-800 pt-4">
      <CampoCodigo
        etiqueta={t('detalle.nuevoCodigo')}
        valor={texto}
        onCambio={setTexto}
        propio={producto.id}
        onEnter={() => void agregar()}
        ayuda={loTiene ? t('detalle.yaLoTiene') : undefined}
        accion={
          <button
            type="button"
            className="btn-secundario grow px-4 sm:grow-0"
            disabled={!limpio || loTiene || falta}
            onClick={() => void agregar()}
          >
            <Ico icono="agregar" />
            {t('comun.agregar')}
          </button>
        }
      />
      {varias && (
        <div className="mt-3">
          <Selector
            etiqueta={t('detalle.suenaComo')}
            value={falta ? '' : unidadId}
            onChange={(e) => {
              setUnidadId(e.target.value);
              // A code already waiting on this answer: the answer is the last thing it needed.
              if (preguntado) void agregar(e.target.value);
            }}
          >
            <option value="" disabled>
              {t('detalle.elijaPresentacion')}
            </option>
            {unidades.map((u) => (
              <option key={u.id} value={u.id}>
                {etiquetaUnidad(u.nombre, u.nombre)}
              </option>
            ))}
          </Selector>
          {preguntado && falta && limpio && (
            <p role="status" className="mt-1.5 text-sm text-amber-300">
              {t('detalle.deCualEs')}
            </p>
          )}
        </div>
      )}
      {error && <p className="mt-2 text-sm text-rose-400">{error}</p>}
    </div>
  );
}

/**
 * The English name, saved when the box loses focus — the same no-save-button rule as prices.
 *
 * The placeholder is what an English screen shows right now when that is the starter
 * catalogue's English, so nobody retypes a name that is already there; typing replaces it,
 * and emptying the box goes back to it.
 */
function NombreEnIngles({ producto }: { producto: Producto }) {
  const t = useT();
  const guardado = producto.nombreEn ?? '';
  const [texto, setTexto] = useState<string | null>(null);
  const deLaSemilla = producto.nombreEn ? null : nombreEnIngles(producto);

  async function guardar() {
    if (texto === null) return;
    const limpio = texto.trim();
    // Recorded before the draft is let go, so the box never flashes the old name back.
    if (limpio !== guardado) {
      await emitir('producto.actualizado', { productoId: producto.id, nombreEn: limpio || null });
    }
    setTexto(null);
  }

  return (
    <Campo
      maxLength={120}
      etiqueta={t('producto.nombreEn')}
      value={texto ?? guardado}
      placeholder={deLaSemilla ?? ''}
      onChange={(e) => setTexto(e.target.value)}
      onBlur={() => void guardar()}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        if (e.key === 'Escape') setTexto(null);
      }}
      autoComplete="off"
      lang="en"
      ayuda={t('producto.ayudaNombreEn')}
    />
  );
}

/**
 * How low the stock gets before it is time to buy more, in the product's own unit. Saved on
 * leaving the box, like the English name. Empty leaves it to «Por comprar», which goes by how
 * fast the product sells.
 */
function StockMinimo({ producto }: { producto: Producto }) {
  const t = useT();
  const guardado = producto.stockMinimo != null ? formatQuantity(producto.stockMinimo) : '';
  const [texto, setTexto] = useState<string | null>(null);
  const nuevo = texto === null || texto.trim() === '' ? null : parseQuantity(texto);
  const invalido = texto !== null && texto.trim() !== '' && (nuevo === null || nuevo < 0);

  async function guardar() {
    if (texto === null || invalido) return;
    if (nuevo !== (producto.stockMinimo ?? null)) {
      await emitir('producto.actualizado', { productoId: producto.id, stockMinimo: nuevo });
    }
    setTexto(null);
  }

  return (
    <div className="mt-4 border-t border-slate-800 pt-4">
      <Campo
        etiqueta={t('detalle.minimo', { unidad: etiquetaUnidad(producto.unidadBase, producto.unidadBase) })}
        value={texto ?? guardado}
        placeholder={t('detalle.minimoAutomatico')}
        inputMode="decimal"
        className="tabular"
        onChange={(e) => setTexto(e.target.value)}
        onBlur={() => void guardar()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') setTexto(null);
        }}
        error={invalido ? t('detalle.minimoInvalido') : undefined}
        ayuda={t('detalle.ayudaMinimo')}
        autoComplete="off"
      />
    </div>
  );
}

function PrecioCelda({
  valor,
  editable,
  onGuardar,
  sugerir,
}: {
  valor: Centavos | null;
  editable: boolean;
  onGuardar: (texto: string) => void;
  sugerir?: () => void;
}) {
  const t = useT();
  const [texto, setTexto] = useState<string | null>(null);

  if (!editable) {
    return (
      <span className="tabular text-sm">{valor !== null ? formatMoney(valor, { symbol: false }) : '—'}</span>
    );
  }

  const mostrado = texto ?? (valor !== null ? String(valor / 100) : '');

  return (
    <div className="flex items-center justify-end gap-1">
      {valor === null && sugerir && (
        <button
          type="button"
          onClick={sugerir}
          title={t('producto.sugerirMargen', { margen: MARGEN_SUGERIDO_BP / 100 })}
          className="rounded px-1.5 py-0.5 text-xs text-slate-500 hover:bg-slate-800 hover:text-slate-300"
          data-sin-icono="tecla"
        >
          {MARGEN_SUGERIDO_BP / 100}%
        </button>
      )}
      <input
        className="campo tabular h-11 w-24 min-h-0 px-2 text-right text-sm"
        inputMode="decimal"
        placeholder="—"
        value={mostrado}
        onChange={(e) => setTexto(e.target.value)}
        onBlur={() => {
          if (texto !== null && texto !== (valor !== null ? String(valor / 100) : '')) onGuardar(texto);
          setTexto(null);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') {
            setTexto(null);
            e.currentTarget.blur();
          }
        }}
      />
    </div>
  );
}
